import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import LanguageSwitcher from '../common/LanguageSwitcher';

interface NavigationItem {
  label: string;
  path: string;
  external?: boolean;
}

const navigationItems: NavigationItem[] = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

/**
 * Main navigation component for marketing website
 * Features responsive design with mobile drawer menu and authentication buttons
 */
const Navigation: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const closeMobileMenu = () => {
    setMobileOpen(false);
  };

  const isActivePath = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const renderDesktopMenu = () => (
    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
      {navigationItems.map((item) => (
        <Link key={item.path} to={item.path} style={{ textDecoration: 'none' }}>
          <Button
            color="inherit"
            data-testid={`nav-${item.label.toLowerCase()}`}
            sx={{
              mx: 1,
              color: isActivePath(item.path) ? 'primary.main' : 'text.primary',
              fontWeight: isActivePath(item.path) ? 600 : 400,
              '&:hover': {
                color: 'primary.main',
                backgroundColor: 'rgba(37, 99, 235, 0.04)',
              },
            }}
          >
            {item.label}
          </Button>
        </Link>
      ))}
      
      {/* Language Switcher */}
      <Box sx={{ ml: 2 }}>
        <LanguageSwitcher />
      </Box>
      
      {/* Authentication Buttons */}
      {user ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
          <Link to="/dashboard" style={{ textDecoration: 'none' }}>
            <Button
              variant="outlined"
              color="primary"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {t('dashboard')}
            </Button>
          </Link>
          <Button
            onClick={logout}
            color="inherit"
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
            }}
          >
            {t('logout')}
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
          <Link to="/login" style={{ textDecoration: 'none' }}>
            <Button
              startIcon={<LoginIcon />}
              color="inherit"
              sx={{
                color: 'text.primary',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: 'rgba(37, 99, 235, 0.04)',
                },
              }}
            >
              {t('login')}
            </Button>
          </Link>
          <Link to="/register" style={{ textDecoration: 'none' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PersonAddIcon />}
              data-testid="nav-cta-button"
              sx={{
                px: 3,
                py: 1,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)',
                },
              }}
            >
              {t('register')}
            </Button>
          </Link>
        </Box>
      )}
    </Box>
  );

  const renderMobileMenu = () => (
    <Drawer
      variant="temporary"
      anchor="right"
      open={mobileOpen}
      onClose={handleDrawerToggle}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile
      }}
      sx={{
        display: { xs: 'block', md: 'none' },
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: 280,
          bgcolor: 'background.paper',
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="div" color="primary.main" fontWeight={700}>
            VegaDigital
          </Typography>
          <IconButton
            onClick={handleDrawerToggle}
            data-testid="mobile-menu-close"
            sx={{ color: 'text.primary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <List>
          {navigationItems.map((item) => (
            <Link key={item.path} to={item.path} style={{ textDecoration: 'none', color: 'inherit' }}>
              <ListItem
                onClick={closeMobileMenu}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                sx={{
                  borderRadius: 1,
                  mb: 1,
                  color: isActivePath(item.path) ? 'primary.main' : 'text.primary',
                  backgroundColor: isActivePath(item.path) ? 'rgba(37, 99, 235, 0.04)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(37, 99, 235, 0.04)',
                  },
                }}
              >
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActivePath(item.path) ? 600 : 400,
                  }}
                />
              </ListItem>
            </Link>
          ))}
          
          {/* Language Switcher */}
          <ListItem sx={{ mt: 2, mb: 1 }}>
            <LanguageSwitcher />
          </ListItem>
          
          {/* Mobile Authentication */}
          {user ? (
            <>
              <ListItem sx={{ mt: 1 }}>
                <Link to="/dashboard" style={{ textDecoration: 'none', width: '100%' }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onClick={closeMobileMenu}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: 'none',
                    }}
                  >
                    {t('dashboard')}
                  </Button>
                </Link>
              </ListItem>
              <ListItem>
                <Button
                  onClick={() => {
                    logout();
                    closeMobileMenu();
                  }}
                  color="inherit"
                  fullWidth
                  sx={{
                    py: 1.5,
                    color: 'text.secondary',
                    textTransform: 'none',
                  }}
                >
                  {t('logout')}
                </Button>
              </ListItem>
            </>
          ) : (
            <>
              <ListItem sx={{ mt: 1 }}>
                <Link to="/login" style={{ textDecoration: 'none', width: '100%' }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    startIcon={<LoginIcon />}
                    onClick={closeMobileMenu}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: 'none',
                    }}
                  >
                    {t('login')}
                  </Button>
                </Link>
              </ListItem>
              <ListItem>
                <Link to="/register" style={{ textDecoration: 'none', width: '100%' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    startIcon={<PersonAddIcon />}
                    data-testid="mobile-nav-cta-button"
                    onClick={closeMobileMenu}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
                    }}
                  >
                    {t('register')}
                  </Button>
                </Link>
              </ListItem>
            </>
          )}
        </List>
      </Box>
    </Drawer>
  );

  return (
    <AppBar
      position="sticky"
      elevation={1}
      data-testid="main-navigation"
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
      }}
    >
      <Toolbar sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Logo */}
        <Link to="/" style={{ textDecoration: 'none', flexGrow: 1 }}>
          <Typography
            variant="h6"
            data-testid="logo"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              '&:hover': {
                color: 'primary.dark',
            },
          }}
        >
          VegaDigital Solutions
        </Typography>
        </Link>

        {/* Desktop Navigation */}
        {renderDesktopMenu()}

        {/* Mobile Menu Button */}
        <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
          <IconButton
            color="inherit"
            aria-label="open menu"
            edge="start"
            onClick={handleDrawerToggle}
            data-testid="mobile-menu-button"
            sx={{ color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Mobile Drawer */}
        {renderMobileMenu()}
      </Toolbar>
    </AppBar>
  );
};

export default Navigation;